/**
 * Bingo — motion vocabulary.
 *
 * The objects in this world are heavier and friendlier than Five's letterpress
 * tiles. A daub is an ink stamp pressed down by hand — it lands with a firm,
 * slightly squashy settle (low stiffness, generous mass), not a sharp click.
 * The called-number medallion swings up on a brass rail. The win is a warm
 * GATHER — the hall light blooms and a banner settles in from above — never a
 * burst of particles.
 *
 * These constants are deliberately different from Five's and Mahjong's:
 * the daub spring is softer and heavier than any tile spring; the call spring
 * has a visible swing; the banner spring lands like a hung sign.
 */

/** The whole composition settles in on entry. */
export const pageSpring = {
  type: 'spring' as const,
  stiffness: 180,
  damping: 26,
  mass: 1.2,
};

/** Hub card settle — friendly, a little heavy. */
export const cardSpring = {
  type: 'spring' as const,
  stiffness: 220,
  damping: 24,
  mass: 0.95,
};

/** Hover lift on interactive cards. */
export const hoverSpring = {
  type: 'spring' as const,
  stiffness: 340,
  damping: 24,
};

/**
 * The daub stamp — the signature motion. A press DOWN with an overshoot squash
 * then settle, like a hand pressing a dauber onto paper. Low stiffness + high
 * mass = a soft, weighty landing rather than a pop.
 */
export const daubSpring = {
  type: 'spring' as const,
  stiffness: 420,
  damping: 17,
  mass: 0.7,
};

/** The called-number medallion swinging up into view. Has a visible swing. */
export const callSpring = {
  type: 'spring' as const,
  stiffness: 240,
  damping: 16,
  mass: 1.0,
};

/** A caller-board cell filling when its number is drawn. */
export const fillSpring = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 20,
  mass: 0.8,
};

/** The win banner settling in from above like a hung sign. */
export const bannerSpring = {
  type: 'spring' as const,
  stiffness: 200,
  damping: 18,
  mass: 1.1,
};

/** The gentle "you missed one" nudge pulse on a called-but-undaubed cell. */
export const nudgeSpring = {
  type: 'spring' as const,
  stiffness: 260,
  damping: 14,
};

// ---- Timings (ms) ----------------------------------------------------------

/** Default seconds between auto-calls. Gentle — tunable down/up in the UI. */
export const CALL_INTERVAL_MS = 4500;
export const CALL_INTERVAL_MIN_MS = 2500;
export const CALL_INTERVAL_MAX_MS = 7000;

/** How long the winning-line highlight runs before the banner lands. */
export const WIN_LINE_HOLD_MS = 700;
/** Delay before the win banner rises (after the winning line lights up). */
export const WIN_BANNER_DELAY_MS = 850;
/** Stagger between hub cards. */
export const HUB_STAGGER_S = 0.08;
/** Stagger across the winning line cells as they brighten. */
export const WIN_CELL_STAGGER_MS = 90;
